import { ShieldCheck, Settings2, X } from 'lucide-react';
import { Switch } from './ui/switch';

export type CookiePreferencesState = {
  necessary: boolean;
  preferences: boolean;
  analytics: boolean;
  consentedAt: string;
  version: number;
};

export const COOKIE_PREFERENCES_KEY = 'aql-cookie-preferences-v1';

export const defaultCookiePreferences = (): CookiePreferencesState => ({
  necessary: true,
  preferences: true,
  analytics: false,
  consentedAt: new Date().toISOString(),
  version: 1
});

export const readCookiePreferences = (): CookiePreferencesState | null => {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(COOKIE_PREFERENCES_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as CookiePreferencesState;
    if (typeof parsed?.necessary !== 'boolean') return null;
    return parsed;
  } catch {
    return null;
  }
};

export const saveCookiePreferences = (preferences: CookiePreferencesState) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(preferences));
};

interface CookieConsentBannerProps {
  onAcceptAll: () => void;
  onRejectOptional: () => void;
  onManage: () => void;
}

export function CookieConsentBanner({ onAcceptAll, onRejectOptional, onManage }: CookieConsentBannerProps) {
  return (
    <div className="fixed inset-x-0 bottom-4 z-[90] px-4">
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-4 rounded-[24px] border border-slate-200 bg-white/96 p-4 shadow-[0_24px_60px_-34px_rgba(15,23,42,0.28)] backdrop-blur md:flex-row md:items-end md:justify-between">
        <div className="max-w-2xl">
          <div className="flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl border border-sky-200 bg-sky-50 text-sky-700">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-900">Gestao de cookies</p>
              <p className="text-sm text-slate-600">Usamos armazenamento local para sessao, tema, preferencias e operacao da plataforma. Podes gerir isso a qualquer momento.</p>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={onManage} className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50">
            Gerir cookies
          </button>
          <button type="button" onClick={onRejectOptional} className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100">
            Rejeitar opcionais
          </button>
          <button type="button" onClick={onAcceptAll} className="inline-flex items-center justify-center rounded-2xl border border-sky-200 bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-sky-700">
            Aceitar todos
          </button>
        </div>
      </div>
    </div>
  );
}

interface CookiePreferencesDialogProps {
  isOpen: boolean;
  preferences: CookiePreferencesState;
  onClose: () => void;
  onSave: (preferences: CookiePreferencesState) => void;
}

export function CookiePreferencesDialog({ isOpen, preferences, onClose, onSave }: CookiePreferencesDialogProps) {
  if (!isOpen) return null;

  const setPreference = (key: 'preferences' | 'analytics', value: boolean) => {
    onSave({
      ...preferences,
      [key]: value,
      consentedAt: new Date().toISOString(),
      version: 1
    });
  };

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-slate-950/55 p-4 backdrop-blur-sm">
      <div className="w-full max-w-2xl rounded-[28px] border border-slate-200 bg-white shadow-[0_32px_80px_-46px_rgba(15,23,42,0.42)]">
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-sky-200 bg-sky-50 text-sky-700">
              <Settings2 className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Preferencias de cookies</h2>
              <p className="text-sm text-slate-500">Controla o armazenamento local e as preferencias opcionais desta experiencia.</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="rounded-xl p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-800">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 px-6 py-5">
          <div className="rounded-[22px] border border-slate-200 bg-slate-50/80 p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-slate-900">Cookies estritamente necessarios</p>
                <p className="mt-1 text-sm text-slate-600">Mantem a sessao autenticada, o modo visual e o estado minimo da plataforma. Estao sempre ativos.</p>
              </div>
              <Switch checked disabled />
            </div>
          </div>

          <div className="rounded-[22px] border border-slate-200 bg-white p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-slate-900">Preferencias de utilizador</p>
                <p className="mt-1 text-sm text-slate-600">Guarda escolhas como idioma, filtros, contexto operacional e personalizacao da interface.</p>
              </div>
              <Switch checked={preferences.preferences} onCheckedChange={(value) => setPreference('preferences', Boolean(value))} />
            </div>
          </div>

          <div className="rounded-[22px] border border-slate-200 bg-white p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-slate-900">Medicao e melhoria do produto</p>
                <p className="mt-1 text-sm text-slate-600">Permite recolher sinais anonimizados de uso para melhorar fluxos, desempenho e qualidade da experiencia.</p>
              </div>
              <Switch checked={preferences.analytics} onCheckedChange={(value) => setPreference('analytics', Boolean(value))} />
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-6 py-4">
          <p className="text-xs text-slate-500">Ultima decisao registada: {new Intl.DateTimeFormat('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(preferences.consentedAt))}</p>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={onClose} className="rounded-2xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
